import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, Image, StyleSheet, TextInput, TouchableOpacity, ScrollView,
  ActivityIndicator, useWindowDimensions,
} from 'react-native';
import {
  ref, listAll, getDownloadURL, getMetadata, getBlob, uploadBytes, deleteObject,
  StorageReference,
} from 'firebase/storage';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db, storage } from '../firebase/firebase';
import { RefreshCw, FolderPlus, ArrowRight, Check, Store, Wand2 } from 'lucide-react';

const C = {
  primary: '#21808D', primaryLight: '#e8f5f7',
  danger: '#ef4444', dangerLight: '#fee2e2',
  success: '#16a34a', successLight: '#dcfce7',
  bg: '#0E1512', card: '#fff',
  textPrimary: '#1a2332', textMuted: '#64748b', border: '#e2e8f0',
};

// Per-restaurant sub-folders. Creating a restaurant folder lays down one
// empty sub-folder for each of these photo types.
const PHOTO_TYPES = ['logo', 'cover', 'banner', 'category', 'product', 'features'];

const RESTAURANT_COLLECTION = 'zapee_restaurant_partner';
const PRODUCT_COLLECTION = 'zapee_restaurant_products';

interface PhotoFile {
  fullPath: string;
  name: string;
  url: string;
  folder: string;
}
interface Restaurant { id: string; name: string; }

// Directory portion of a path: "a/b/c.jpg" -> "a/b"; root file -> "(root)".
function dirOf(path: string): string {
  const i = path.lastIndexOf('/');
  return i === -1 ? '(root)' : path.slice(0, i);
}

// Decoded storage object path from a Firebase download URL, else null.
function pathFromUrl(url: string): string | null {
  if (typeof url !== 'string' || !url.includes('/o/')) return null;
  const m = url.match(/\/o\/([^?]+)/);
  if (!m) return null;
  try { return decodeURIComponent(m[1]); } catch { return m[1]; }
}

const isPlainObject = (v: any) =>
  v != null && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype;

const getByPath = (obj: any, path: string) =>
  path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);

// Candidate field names that may hold a restaurant's cover photo.
const COVER_FIELDS = ['media.cover', 'cover', 'media.coverImage', 'coverImage', 'coverPhoto', 'media.coverPhoto'];

// Collect every Firebase Storage download-URL string nested anywhere in a value.
function collectStorageUrls(value: any, out: Set<string>) {
  if (typeof value === 'string') { if (pathFromUrl(value)) out.add(value); }
  else if (Array.isArray(value)) value.forEach(v => collectStorageUrls(v, out));
  else if (isPlainObject(value)) Object.values(value).forEach(v => collectStorageUrls(v, out));
}

// Deep-clone a value, swapping any string found in `repl` for its replacement.
// Non-plain objects (Timestamp, GeoPoint, …) are returned untouched.
function deepReplace(value: any, repl: Map<string, string>): any {
  if (typeof value === 'string') return repl.get(value) ?? value;
  if (Array.isArray(value)) return value.map(v => deepReplace(v, repl));
  if (isPlainObject(value)) {
    const o: any = {};
    for (const [k, v] of Object.entries(value)) o[k] = deepReplace(v, repl);
    return o;
  }
  return value;
}

// A placeholder is a zero-byte folder marker, never a real photo.
function isPlaceholder(r: StorageReference): boolean {
  return r.name === '' || r.fullPath.endsWith('/') || r.name.startsWith('.');
}

// Strip characters Storage paths can't hold; keep "/" so nesting works.
function sanitizeFolder(raw: string): string {
  return raw
    .replace(/[#\[\]*?]/g, '')
    .replace(/\\/g, '/')
    .split('/').map(s => s.trim()).filter(Boolean).join('/');
}

async function listAllFiles(folderRef: StorageReference): Promise<StorageReference[]> {
  const res = await listAll(folderRef);
  let files = [...res.items];
  for (const prefix of res.prefixes) files = files.concat(await listAllFiles(prefix));
  return files;
}

export default function PhotoOrganizer() {
  const { width } = useWindowDimensions();
  const cols = width < 600 ? 2 : width < 1000 ? 3 : width < 1400 ? 4 : 5;

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [folders, setFolders] = useState<string[]>([]);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);

  const [source, setSource] = useState<string>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dest, setDest] = useState<string>('');
  const [newFolder, setNewFolder] = useState('');
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setProgress('Listing storage files…');
      const refs = await listAllFiles(ref(storage));
      const realRefs = refs.filter(r => !isPlaceholder(r));

      // All folder prefixes — from real files AND placeholder-only (empty) folders.
      const folderSet = new Set<string>();
      refs.forEach(r => folderSet.add(dirOf(r.fullPath)));

      setProgress(`Loading ${realRefs.length} photos…`);
      const resolved = await Promise.all(realRefs.map(async (r) => {
        let url = '';
        try { url = await getDownloadURL(r); } catch { /* ignore */ }
        return { fullPath: r.fullPath, name: r.name, url, folder: dirOf(r.fullPath) } as PhotoFile;
      }));

      setProgress('Loading restaurants…');
      let rests: Restaurant[] = [];
      try {
        const snap = await getDocs(collection(db, RESTAURANT_COLLECTION));
        rests = snap.docs
          .map(d => ({ id: d.id, name: (d.data() as any)?.storename || d.id }))
          .filter(r => r.name)
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch { /* ignore */ }

      setPhotos(resolved.sort((a, b) => a.fullPath.localeCompare(b.fullPath)));
      setFolders(Array.from(folderSet).sort());
      setRestaurants(rests);
      setSelected(new Set());
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
      setProgress('');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Folder creation ──
  const createFolders = async (prefixes: string[]) => {
    const fresh = prefixes.map(sanitizeFolder).filter(Boolean);
    if (fresh.length === 0) return;
    setWorking(true);
    try {
      await Promise.all(fresh.map(p =>
        uploadBytes(ref(storage, `${p}/.keep`), new Blob([]), { contentType: 'application/octet-stream' })
          .catch(() => {})
      ));
      setFolders(prev => Array.from(new Set([...prev, ...fresh])).sort());
      if (fresh.length === 1) setDest(fresh[0]);
    } catch (e: any) {
      window.alert(`Failed to create folder: ${e?.message || e}`);
    } finally {
      setWorking(false);
    }
  };

  const createManualFolder = async () => {
    const name = sanitizeFolder(newFolder);
    if (!name) return;
    await createFolders([name]);
    setNewFolder('');
  };

  const restaurantSubfolders = (name: string) =>
    PHOTO_TYPES.map(t => `${sanitizeFolder(name)}/${t}`);

  // Does this restaurant already have its directory laid down?
  const hasFolder = (name: string) => {
    const root = sanitizeFolder(name);
    return folders.some(f => f === root || f.startsWith(root + '/'));
  };

  const createForRestaurant = async (name: string) => {
    await createFolders(restaurantSubfolders(name));
  };

  const createAllRestaurantFolders = async () => {
    if (restaurants.length === 0) return;
    const missing = restaurants.filter(r => !hasFolder(r.name));
    const targets = missing.length ? missing : restaurants;
    const total = targets.length * PHOTO_TYPES.length;
    if (!window.confirm(
      `Make directories for ${targets.length} restaurant(s) — ${PHOTO_TYPES.length} sub-folders each (${total} folders)?`
    )) return;
    await createFolders(targets.flatMap(r => restaurantSubfolders(r.name)));
    window.alert('Done. Restaurant directories created.');
  };

  // ── Selection ──
  const visiblePhotos = source === 'all' ? photos : photos.filter(p => p.folder === source);
  const toggle = (path: string) =>
    setSelected(prev => {
      const n = new Set(prev);
      n.has(path) ? n.delete(path) : n.add(path);
      return n;
    });
  const selectAllVisible = () =>
    setSelected(prev => {
      const n = new Set(prev);
      visiblePhotos.forEach(p => n.add(p.fullPath));
      return n;
    });
  const clearSelection = () => setSelected(new Set());

  // ── Move ──
  const moveSelected = async () => {
    if (!dest) { window.alert('Pick a destination folder.'); return; }
    const chosen = photos.filter(p => selected.has(p.fullPath));
    const toMove = chosen.filter(p => p.folder !== dest);
    if (toMove.length === 0) { window.alert('Nothing to move (or already in that folder).'); return; }
    if (!window.confirm(`Move ${toMove.length} photo(s) into "${dest}"?`)) return;

    setWorking(true);
    const existingNames = new Set(photos.filter(p => p.folder === dest).map(p => p.name));
    const failed: string[] = [];
    let done = 0;
    for (const f of toMove) {
      try {
        const srcRef = ref(storage, f.fullPath);
        let contentType: string | undefined;
        try { contentType = (await getMetadata(srcRef)).contentType || undefined; } catch { /* ignore */ }
        const blob = await getBlob(srcRef);

        let name = f.name;
        if (existingNames.has(name)) {
          const dot = name.lastIndexOf('.');
          const base = dot > 0 ? name.slice(0, dot) : name;
          const ext = dot > 0 ? name.slice(dot) : '';
          name = `${base}_${done}_${blob.size}${ext}`;
        }
        existingNames.add(name);

        await uploadBytes(ref(storage, `${dest}/${name}`), blob, contentType ? { contentType } : undefined);
        await deleteObject(srcRef);
        done++;
        setProgress(`Moved ${done}/${toMove.length}…`);
      } catch {
        failed.push(f.fullPath);
      }
    }
    setWorking(false);
    setProgress('');
    await load();
    if (failed.length) {
      window.alert(
        `${failed.length} of ${toMove.length} photo(s) could not be moved.\n` +
        `If all failed, the Storage bucket likely needs CORS enabled for this origin (downloads).`
      );
    }
  };

  // ── Auto-sort: move each restaurant's images into its sub-folders and
  //    update the Firestore references so nothing breaks. ──
  //      restaurant logo (media.logo / image)         -> <name>/logo
  //      restaurant cover (media.cover / cover / …)    -> <name>/cover
  //      restaurant banners (carousel)                -> <name>/banner
  //      restaurant categories[] (nested images)      -> <name>/category
  //      product.image   (linked by product.branch)   -> <name>/product
  //      product.featureImages                        -> <name>/features
  const autoSortByRestaurant = async () => {
    setWorking(true);
    setProgress('Reading restaurants & products…');
    try {
      const [restSnap, prodSnap] = await Promise.all([
        getDocs(collection(db, RESTAURANT_COLLECTION)),
        getDocs(collection(db, PRODUCT_COLLECTION)),
      ]);

      // storeuid -> sanitized folder root
      const rootByStore = new Map<string, string>();
      const restDocs = restSnap.docs.map(d => {
        const data: any = d.data();
        const store = String(data.storeuid || data.uid || data.id || d.id);
        const root = sanitizeFolder(data.storename || data.name || '');
        if (root) rootByStore.set(store, root);
        return { id: d.id, data, root };
      });

      interface Task {
        col: string; id: string; root: string;
        singles: { field: string; alt?: string; url: string; type: string }[];
        arrays: { field: string; alt?: string; urls: string[]; type: string }[];
        // deeps: a whole nested field (e.g. the restaurant `categories` array of
        // objects) whose image URLs get moved + replaced in place.
        deeps: { field: string; type: string; value: any }[];
      }
      const tasks: Task[] = [];

      for (const r of restDocs) {
        if (!r.root) continue;
        const logo = r.data?.media?.logo || r.data?.image || '';
        const banners = (r.data?.media?.bannerImages || r.data?.bannerImages || []) as string[];
        const categories = r.data?.categories;

        const singles: Task['singles'] = [];
        if (logo) singles.push({ field: 'image', alt: 'media.logo', url: logo, type: 'logo' });
        // Cover photo — field name varies; write back to whichever one held it.
        for (const f of COVER_FIELDS) {
          const v = getByPath(r.data, f);
          if (typeof v === 'string' && pathFromUrl(v)) { singles.push({ field: f, url: v, type: 'cover' }); break; }
        }

        tasks.push({
          col: RESTAURANT_COLLECTION, id: r.id, root: r.root,
          singles,
          arrays: banners?.length ? [{ field: 'bannerImages', alt: 'media.bannerImages', urls: banners, type: 'banner' }] : [],
          deeps: (categories && (Array.isArray(categories) ? categories.length : true))
            ? [{ field: 'categories', type: 'category', value: categories }] : [],
        });
      }
      for (const p of prodSnap.docs) {
        const data: any = p.data();
        const root = rootByStore.get(String(data.branch || ''));
        if (!root) continue;
        const img = data.image || '';
        const feats = (data.featureImages || []) as string[];
        tasks.push({
          col: PRODUCT_COLLECTION, id: p.id, root,
          singles: img ? [{ field: 'image', url: img, type: 'product' }] : [],
          arrays: feats?.length ? [{ field: 'featureImages', urls: feats, type: 'features' }] : [],
          deeps: [],
        });
      }

      // Count what actually needs moving (storage file not already in its folder).
      const needsMove = (url: string, type: string, root: string) => {
        const op = pathFromUrl(url);
        return !!op && dirOf(op) !== `${root}/${type}`;
      };
      const deepUrls = (d: { value: any }) => {
        const s = new Set<string>(); collectStorageUrls(d.value, s); return Array.from(s);
      };
      let planCount = 0;
      for (const t of tasks) {
        t.singles.forEach(s => { if (needsMove(s.url, s.type, t.root)) planCount++; });
        t.arrays.forEach(a => a.urls.forEach(u => { if (needsMove(u, a.type, t.root)) planCount++; }));
        t.deeps.forEach(d => deepUrls(d).forEach(u => { if (needsMove(u, d.type, t.root)) planCount++; }));
      }
      if (planCount === 0) {
        setWorking(false); setProgress('');
        window.alert('Everything is already sorted — nothing to move.');
        return;
      }
      if (!window.confirm(
        `Move ${planCount} image(s) into their restaurant folders and update the database references?\n\n` +
        `This reorganizes live images (logos, banners, product & feature photos).`
      )) { setWorking(false); setProgress(''); return; }

      let moved = 0, failed = 0, docsUpdated = 0;
      const usedNames = new Set<string>();

      const moveFile = async (oldPath: string, destFolder: string): Promise<string | null> => {
        const srcRef = ref(storage, oldPath);
        let base = oldPath.split('/').pop() || `file_${moved}`;
        let key = `${destFolder}/${base}`;
        if (usedNames.has(key)) {
          const dot = base.lastIndexOf('.');
          base = (dot > 0 ? base.slice(0, dot) : base) + `_${moved}` + (dot > 0 ? base.slice(dot) : '');
          key = `${destFolder}/${base}`;
        }
        let contentType: string | undefined;
        try { contentType = (await getMetadata(srcRef)).contentType || undefined; } catch { /* ignore */ }
        const blob = await getBlob(srcRef);
        await uploadBytes(ref(storage, key), blob, contentType ? { contentType } : undefined);
        const newUrl = await getDownloadURL(ref(storage, key));
        usedNames.add(key);
        return newUrl;
      };

      for (const t of tasks) {
        const repl = new Map<string, string>();
        const oldPaths: string[] = [];
        const items = [
          ...t.singles.map(s => ({ url: s.url, type: s.type })),
          ...t.arrays.flatMap(a => a.urls.map(u => ({ url: u, type: a.type }))),
          ...t.deeps.flatMap(d => deepUrls(d).map(u => ({ url: u, type: d.type }))),
        ];
        for (const it of items) {
          if (repl.has(it.url)) continue;
          const op = pathFromUrl(it.url);
          if (!op) continue;
          const destFolder = `${t.root}/${it.type}`;
          if (dirOf(op) === destFolder) continue;
          setProgress(`Moving ${moved + 1}/${planCount}…`);
          try {
            const newUrl = await moveFile(op, destFolder);
            if (newUrl) { repl.set(it.url, newUrl); oldPaths.push(op); moved++; }
          } catch { failed++; }
        }
        if (repl.size === 0) continue;

        const upd: any = {};
        for (const s of t.singles) {
          if (repl.has(s.url)) { upd[s.field] = repl.get(s.url); if (s.alt) upd[s.alt] = repl.get(s.url); }
        }
        for (const a of t.arrays) {
          if (a.urls.some(u => repl.has(u))) {
            const newArr = a.urls.map(u => repl.get(u) ?? u);
            upd[a.field] = newArr; if (a.alt) upd[a.alt] = newArr;
          }
        }
        for (const d of t.deeps) {
          if (deepUrls(d).some(u => repl.has(u))) {
            upd[d.field] = deepReplace(d.value, repl);
          }
        }
        try {
          await updateDoc(doc(db, t.col, t.id), upd);
          docsUpdated++;
          await Promise.all(oldPaths.map(p => deleteObject(ref(storage, p)).catch(() => {})));
        } catch {
          // DB update failed: leave originals + references untouched (still valid).
          failed += repl.size;
        }
      }

      setWorking(false); setProgress('');
      await load();
      window.alert(
        `Auto-sort complete.\nMoved: ${moved}\nDocuments updated: ${docsUpdated}\nFailed: ${failed}` +
        (failed ? `\n\nIf many failed, the Storage bucket likely needs CORS enabled for downloads from this origin.` : '')
      );
    } catch (e: any) {
      setWorking(false); setProgress('');
      window.alert(`Auto-sort failed: ${e?.message || e}`);
    }
  };

  const folderCount = (f: string) => photos.filter(p => p.folder === f).length;
  const selectedCount = selected.size;

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={C.primary} />
        <Text style={styles.progressText}>{progress || 'Loading…'}</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: 40 }}>
      {/* Header */}
      <View style={styles.header}>
        <span className="material-icons-round" style={{ fontSize: 20, color: '#4ECDC4', lineHeight: 1, flexShrink: 0 }}>folder_open</span>
        <View style={{ flex: 1 }}>
          <Text style={styles.title}>Photo Organizer</Text>
          <Text style={styles.subtitle}>{photos.length} photos · {folders.length} folders</Text>
        </View>
        <TouchableOpacity style={styles.refreshBtn} onPress={load} disabled={working}>
          <RefreshCw size={16} color="#fff" />
          <Text style={styles.refreshTxt}>Refresh</Text>
        </TouchableOpacity>
      </View>

      {error && <View style={styles.errorBox}><Text style={styles.errorTxt}>{error}</Text></View>}
      {working && (
        <View style={styles.workingBox}>
          <ActivityIndicator size="small" color={C.primary} />
          <Text style={styles.workingTxt}>{progress || 'Working…'}</Text>
        </View>
      )}

      {/* ── Create folder ── */}
      <View style={styles.panel}>
        <Text style={styles.panelTitle}>Create folder</Text>

        <View style={styles.rowWrap}>
          <TextInput
            style={styles.input}
            placeholder="Folder name (e.g. Pizza Hut/logo)"
            placeholderTextColor="#94a3b8"
            value={newFolder}
            onChangeText={setNewFolder}
          />
          <TouchableOpacity style={styles.primaryBtn} onPress={createManualFolder} disabled={working}>
            <FolderPlus size={15} color="#fff" />
            <Text style={styles.primaryBtnTxt}>Create</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.divider} />

        {/* All restaurants in the project + one button to build every directory */}
        <View style={styles.rowWrap}>
          <Text style={styles.panelSub}>
            <Store size={13} color={C.textMuted} />  Restaurants ({restaurants.length}) — each gets: {PHOTO_TYPES.join(', ')}
          </Text>
          <View style={{ flex: 1 }} />
          <TouchableOpacity style={styles.primaryBtn} onPress={createAllRestaurantFolders} disabled={working || restaurants.length === 0}>
            <FolderPlus size={15} color="#fff" />
            <Text style={styles.primaryBtnTxt}>Make directories for all</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.restList}>
          {restaurants.length === 0 && <Text style={styles.muted}>No restaurants found.</Text>}
          {restaurants.map(r => {
            const done = hasFolder(r.name);
            return (
              <TouchableOpacity
                key={r.id}
                style={[styles.restChip, done && styles.restChipDone]}
                onPress={() => createForRestaurant(r.name)}
                disabled={working}
              >
                {done
                  ? <Check size={13} color={C.success} />
                  : <FolderPlus size={13} color={C.primary} />}
                <Text style={[styles.restChipTxt, done && { color: C.success }]} numberOfLines={1}>
                  {r.name}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>

      {/* ── Auto-sort ── */}
      <View style={styles.panel}>
        <View style={styles.rowWrap}>
          <View style={{ flex: 1, minWidth: 220 }}>
            <Text style={styles.panelTitle}>Auto-sort images into restaurant folders</Text>
            <Text style={styles.muted}>
              Moves each restaurant's logo → logo, banners → banner, product photos → product,
              feature photos → features — and updates the database so live images keep working.
            </Text>
          </View>
          <TouchableOpacity style={styles.autoBtn} onPress={autoSortByRestaurant} disabled={working}>
            <Wand2 size={16} color="#fff" />
            <Text style={styles.autoBtnTxt}>Auto-sort now</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* ── Move toolbar ── */}
      <View style={styles.panel}>
        <Text style={styles.panelTitle}>Move photos</Text>
        <View style={styles.rowWrap}>
          <TouchableOpacity style={styles.outlineBtn} onPress={selectAllVisible}>
            <Check size={14} color={C.primary} />
            <Text style={styles.outlineBtnTxt}>Mark all ({visiblePhotos.length})</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.outlineBtn} onPress={clearSelection} disabled={selectedCount === 0}>
            <Text style={styles.outlineBtnTxt}>Clear</Text>
          </TouchableOpacity>
          <Text style={styles.selectedTxt}>{selectedCount} selected</Text>
          <View style={{ flex: 1 }} />
          <Text style={styles.destLabel}>
            To: <Text style={{ fontWeight: '800', color: C.primary }}>{dest || '— pick below —'}</Text>
          </Text>
          <TouchableOpacity
            style={[styles.moveBtn, (!dest || selectedCount === 0) && styles.moveBtnDisabled]}
            onPress={moveSelected}
            disabled={working || !dest || selectedCount === 0}
          >
            <ArrowRight size={15} color="#fff" />
            <Text style={styles.moveBtnTxt}>Move</Text>
          </TouchableOpacity>
        </View>

        {/* Destination folder chips */}
        <Text style={[styles.panelSub, { marginTop: 10 }]}>Destination folder</Text>
        <View style={styles.chipsWrap}>
          {folders.length === 0 && <Text style={styles.muted}>No folders yet — create one above.</Text>}
          {folders.map(f => (
            <TouchableOpacity
              key={f}
              style={[styles.destChip, dest === f && styles.destChipActive]}
              onPress={() => setDest(f)}
            >
              <Text style={[styles.destChipTxt, dest === f && styles.destChipTxtActive]}>{f}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* ── Source folder tabs ── */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabsRow}>
        <Tab label={`All (${photos.length})`} active={source === 'all'} onPress={() => setSource('all')} />
        {folders.map(f => (
          <Tab key={f} label={`${f} (${folderCount(f)})`} active={source === f} onPress={() => setSource(f)} />
        ))}
      </ScrollView>

      {/* ── Photo grid ── */}
      {visiblePhotos.length === 0 && (
        <View style={styles.center}><Text style={styles.muted}>No photos in this folder.</Text></View>
      )}
      <View style={styles.grid}>
        {visiblePhotos.map(p => {
          const isSel = selected.has(p.fullPath);
          return (
            <View key={p.fullPath} style={[styles.cardWrap, { width: `${100 / cols}%` }]}>
              <TouchableOpacity
                activeOpacity={0.8}
                style={[styles.card, isSel && styles.cardSelected]}
                onPress={() => toggle(p.fullPath)}
              >
                <View style={styles.thumbWrap}>
                  {p.url
                    ? <Image source={{ uri: p.url }} style={styles.thumb} resizeMode="cover" />
                    : <View style={[styles.thumb, styles.thumbFallback]} />}
                  <View style={[styles.check, isSel && styles.checkOn]}>
                    {isSel && <Check size={14} color="#fff" />}
                  </View>
                </View>
                <View style={styles.cardBody}>
                  <Text style={styles.fileName} numberOfLines={1}>{p.name}</Text>
                  <Text style={styles.filePath} numberOfLines={1}>{p.folder}</Text>
                </View>
              </TouchableOpacity>
            </View>
          );
        })}
      </View>
    </ScrollView>
  );
}

function Tab({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <TouchableOpacity style={[styles.tab, active && styles.tabActive]} onPress={onPress}>
      <Text style={[styles.tabTxt, active && styles.tabTxtActive]}>{label}</Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.bg, padding: 16 },
  center: { justifyContent: 'center', alignItems: 'center', padding: 40, minHeight: 160 },
  progressText: { marginTop: 14, color: C.textMuted, fontSize: 14 },
  muted: { color: C.textMuted, fontSize: 13 },

  header: { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap', backgroundColor: '#0A1712', marginTop: -16, marginHorizontal: -16, marginBottom: 14, paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: 'rgba(33,128,141,0.18)' },
  title: { fontSize: 15, fontWeight: '800', color: '#fff' },
  subtitle: { fontSize: 11, color: '#8BAAB8', marginTop: 1 },
  refreshBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: C.primary, paddingHorizontal: 14, paddingVertical: 9, borderRadius: 8,
  },
  refreshTxt: { color: '#fff', fontWeight: '600', fontSize: 13 },

  errorBox: { backgroundColor: C.dangerLight, borderRadius: 8, padding: 12, marginBottom: 12 },
  errorTxt: { color: C.danger, fontSize: 13 },
  workingBox: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: C.primaryLight, borderRadius: 8, padding: 10, marginBottom: 12,
  },
  workingTxt: { color: C.primary, fontSize: 13, fontWeight: '600' },

  panel: {
    backgroundColor: C.card, borderRadius: 12, borderWidth: 1, borderColor: C.border,
    padding: 14, marginBottom: 14,
  },
  panelTitle: { fontSize: 15, fontWeight: '700', color: C.textPrimary, marginBottom: 10 },
  panelSub: { fontSize: 13, color: C.textMuted, marginBottom: 8, fontWeight: '600' },
  divider: { height: 1, backgroundColor: C.border, marginVertical: 12 },
  rowWrap: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },

  input: {
    flexGrow: 1, minWidth: 200, borderWidth: 1, borderColor: C.border, borderRadius: 8,
    paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, color: C.textPrimary, backgroundColor: '#17201B',
  },
  primaryBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: C.primary, paddingHorizontal: 14, paddingVertical: 10, borderRadius: 8,
  },
  primaryBtnTxt: { color: '#fff', fontWeight: '700', fontSize: 13 },
  outlineBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: '#17201B', borderWidth: 1, borderColor: C.primary,
    paddingHorizontal: 14, paddingVertical: 9, borderRadius: 8,
  },
  outlineBtnTxt: { color: C.primary, fontWeight: '700', fontSize: 13 },

  selectedTxt: { fontSize: 13, fontWeight: '700', color: C.textPrimary },
  destLabel: { fontSize: 13, color: C.textMuted },
  moveBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: C.success, paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8,
  },
  moveBtnDisabled: { backgroundColor: '#94a3b8' },
  moveBtnTxt: { color: '#fff', fontWeight: '800', fontSize: 13 },

  restList: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  restChip: {
    flexDirection: 'row', alignItems: 'center', gap: 6, maxWidth: 240,
    paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8,
    backgroundColor: C.bg, borderWidth: 1, borderColor: C.border,
  },
  restChipDone: { backgroundColor: C.successLight, borderColor: C.success },
  restChipTxt: { fontSize: 13, color: C.textPrimary, fontWeight: '600' },

  autoBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: '#7c3aed', paddingHorizontal: 16, paddingVertical: 11, borderRadius: 8,
  },
  autoBtnTxt: { color: '#fff', fontWeight: '800', fontSize: 13 },

  chipsWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  destChip: {
    paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8,
    backgroundColor: C.bg, borderWidth: 1, borderColor: C.border,
  },
  destChipActive: { backgroundColor: C.primaryLight, borderColor: C.primary },
  destChipTxt: { fontSize: 12, color: C.textMuted, fontWeight: '600' },
  destChipTxtActive: { color: C.primary },

  tabsRow: { flexDirection: 'row', marginBottom: 14 },
  tab: {
    paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20, marginRight: 8,
    backgroundColor: C.card, borderWidth: 1, borderColor: C.border,
  },
  tabActive: { backgroundColor: C.primary, borderColor: C.primary },
  tabTxt: { fontSize: 13, color: C.textMuted, fontWeight: '600' },
  tabTxtActive: { color: '#fff' },

  grid: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -6 },
  cardWrap: { paddingHorizontal: 6, marginBottom: 12 },
  card: { backgroundColor: C.card, borderRadius: 12, overflow: 'hidden', borderWidth: 2, borderColor: C.border },
  cardSelected: { borderColor: C.primary },
  thumbWrap: { position: 'relative', width: '100%', aspectRatio: 1, backgroundColor: '#f1f5f9' },
  thumb: { width: '100%', height: '100%' },
  thumbFallback: { backgroundColor: '#e2e8f0' },
  check: {
    position: 'absolute', top: 8, right: 8, width: 24, height: 24, borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.9)', borderWidth: 2, borderColor: '#cbd5e1',
    justifyContent: 'center', alignItems: 'center',
  },
  checkOn: { backgroundColor: C.primary, borderColor: C.primary },
  cardBody: { padding: 10 },
  fileName: { fontSize: 13, fontWeight: '600', color: C.textPrimary },
  filePath: { fontSize: 11, color: C.textMuted, marginTop: 2 },
});
